import React, { useState, useEffect } from "react";
import {
  Box,
  Divider,
  Icon,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { generateAPIBatchNumber } from "../ProcessedItemFormDialog";

interface ProductionDetailsStepProps {
  fullDocumnet: any;
  selectedDocument: any;
  handleUpdateFullDoc: (value: any, field: string) => void;
}

export const APIProductionDetailsStep: React.FC<ProductionDetailsStepProps> = ({
  fullDocumnet,
  selectedDocument,
  handleUpdateFullDoc,
}) => {
  const [shelfLife, setShelfLife] = useState(7);
  const [shelfLifeUnit, setShelfLifeUnit] = useState("day");
  const [expiryDateError, setExpiryDateError] = useState<string | null>(null);

  const validateAndUpdateExpiryDate = (newExpiryDate: number) => {
    const productionDate = dayjs(fullDocumnet.production_date);
    const expiryDate = dayjs(newExpiryDate);

    if (expiryDate.isBefore(productionDate)) {
      setExpiryDateError("Expiry date cannot be before production date");
      return false;
    }
    setExpiryDateError(null);
    handleUpdateFullDoc(newExpiryDate, "expiry_date");
    return true;
  };

  const updateExpiryDate = (duration: number, unit: string) => {
    const currentDate = dayjs(fullDocumnet.production_date);
    const newDate =
      unit === "hour"
        ? currentDate.add(duration, unit as dayjs.ManipulateType)
        : currentDate
            .hour(12)
            .minute(0)
            .add(duration, unit as dayjs.ManipulateType);

    validateAndUpdateExpiryDate(newDate.valueOf());
  };

  const getExpiryDateInfo = () => {
    const productionDate = dayjs(fullDocumnet.production_date);
    const expiryDate = dayjs(fullDocumnet.expiry_date);
    const diffInHours = expiryDate.diff(productionDate, "hour");
    const diffInDays = expiryDate.diff(productionDate, "day");
    const diffInMonths = expiryDate.diff(productionDate, "month");

    if (diffInHours < 24) {
      return `${diffInHours} hours`;
    } else if (diffInDays < 31) {
      return `${diffInDays} days`;
    } else {
      return `${diffInMonths} months`;
    }
  };

  // Validate expiry date whenever production date changes
  useEffect(() => {
    validateAndUpdateExpiryDate(fullDocumnet.expiry_date);
  }, [fullDocumnet.production_date]);

  return (
    <Box sx={{ height: "100%" }}>
      <Stack spacing={4}>
        {/* Batch Number Section */}
        <Box>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Batch Number"
              value={fullDocumnet.api_batch_id}
              onChange={(e) =>
                handleUpdateFullDoc(e.target.value, "api_batch_id")
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon sx={{ color: "#0891B2" }}>inventory_2</Icon>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={"Use auto-generated"}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleUpdateFullDoc(
                            generateAPIBatchNumber(),
                            "api_batch_id"
                          )
                        }
                        sx={{
                          color: "#0891B2",
                          "&:hover": {
                            bgcolor: alpha("#0891B2", 0.04),
                          },
                        }}
                      >
                        <Icon>{"autorenew"}</Icon>
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                  "&:hover": {
                    bgcolor: alpha("#0891B2", 0.02),
                  },
                  "&.Mui-focused": {
                    bgcolor: "white",
                    boxShadow: `0 0 0 2px ${alpha("#0891B2", 0.2)}`,
                  },
                  "& input": {
                    cursor: "text",
                  },
                  "& input:not([readonly])": {
                    cursor: "text",
                    userSelect: "text",
                  },
                },
              }}
              inputProps={{
                readOnly: false,
              }}
            />
          </Stack>
        </Box>

        {/* Production Timeline Section */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 600,
              color: "text.primary",
              mb: 0.5,
            }}
          >
            Production Timeline
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            Set production and expiry dates for the batch
          </Typography>

          <Stack spacing={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ position: "relative" }}>
                <DatePicker
                  label="Production Date & Time"
                  value={dayjs(fullDocumnet.production_date)}
                  onChange={(date) => {
                    if (date) {
                      handleUpdateFullDoc(date.valueOf(), "production_date");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon sx={{ color: "#0891B2" }}>event</Icon>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          "&:hover": {
                            bgcolor: alpha("#0891B2", 0.02),
                          },
                          "&.Mui-focused": {
                            bgcolor: "white",
                            boxShadow: `0 0 0 2px ${alpha("#0891B2", 0.2)}`,
                          },
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </LocalizationProvider>

            <Box
              sx={{
                p: 2,
                bgcolor: alpha("#0891B2", 0.04),
                borderRadius: 1,
                border: "1px solid",
                borderColor: alpha("#0891B2", 0.1),
              }}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#0891B2",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Icon fontSize="small">timer</Icon>
                    Shelf Life
                  </Typography>
                  <Chip
                    label={`Duration: ${getExpiryDateInfo()}`}
                    size="small"
                    sx={{
                      bgcolor: "white",
                      border: "1px solid",
                      borderColor: alpha("#0891B2", 0.2),
                    }}
                  />
                </Box>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Duration"
                    type="number"
                    value={shelfLife}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon sx={{ color: "#0891B2" }}>schedule</Icon>
                        </InputAdornment>
                      ),
                      inputProps: { min: 1 },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        "&:hover": {
                          bgcolor: alpha("#0891B2", 0.02),
                        },
                        "&.Mui-focused": {
                          bgcolor: "white",
                          boxShadow: `0 0 0 2px ${alpha("#0891B2", 0.2)}`,
                        },
                      },
                    }}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value > 0) {
                        setShelfLife(value);
                        updateExpiryDate(value, shelfLifeUnit);
                      }
                    }}
                  />
                  <TextField
                    select
                    value={shelfLifeUnit}
                    fullWidth
                    label="Unit"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon sx={{ color: "#0891B2" }}>straighten</Icon>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        "&:hover": {
                          bgcolor: alpha("#0891B2", 0.02),
                        },
                        "&.Mui-focused": {
                          bgcolor: "white",
                          boxShadow: `0 0 0 2px ${alpha("#0891B2", 0.2)}`,
                        },
                      },
                    }}
                    onChange={(e) => {
                      setShelfLifeUnit(e.target.value);
                      updateExpiryDate(shelfLife, e.target.value);
                    }}
                  >
                    <ListSubheader
                      sx={{
                        fontFamily: "Montserrat",
                        color: "#0891B2",
                        fontWeight: 600,
                      }}
                    >
                      Short Term
                    </ListSubheader>
                    <MenuItem value="hour">Hours</MenuItem>
                    <MenuItem value="day">Days</MenuItem>
                    <Divider />
                    <ListSubheader
                      sx={{
                        fontFamily: "Montserrat",
                        color: "#0891B2",
                        fontWeight: 600,
                      }}
                    >
                      Long Term
                    </ListSubheader>
                    <MenuItem value="week">Weeks</MenuItem>
                    <MenuItem value="month">Months</MenuItem>
                    <MenuItem value="year">Years</MenuItem>
                  </TextField>
                </Stack>
              </Stack>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ position: "relative" }}>
                <DatePicker
                  label="Expiry Date & Time"
                  value={dayjs(fullDocumnet.expiry_date)}
                  minDate={dayjs(fullDocumnet.production_date)}
                  onChange={(date) => {
                    if (date) {
                      validateAndUpdateExpiryDate(date.valueOf());
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      error={!!expiryDateError}
                      helperText={expiryDateError}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon sx={{ color: "#0891B2" }}>event_busy</Icon>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          "&:hover": {
                            bgcolor: alpha("#0891B2", 0.02),
                          },
                          "&.Mui-focused": {
                            bgcolor: "white",
                            boxShadow: `0 0 0 2px ${alpha("#0891B2", 0.2)}`,
                          },
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </LocalizationProvider>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};
